import React, { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { 
  Mail,
  MapPin,
  Users,
  Target,
  ArrowRight,
  ExternalLink,
  Building,
  Briefcase,
  Phone
} from "lucide-react";
import { mockData } from "../mock";

// This is Part 2 of the landing page - Domains, Stealth, Partners, Waitlist, FAQ, Contact, Footer
const GodquantumLandingPart2 = () => {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleWaitlistSubmit = (e) => {
    e.preventDefault();
    // Open Google Form in new tab
    window.open(mockData.externalLinks.googleForm, '_blank');
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000); // Reset after 3 seconds
  };

  return (
    <div className="bg-black text-white">
      {/* Application Domains Section */}
      <section id="domains" className="py-24 border-t border-white/5 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="display-medium mb-6">Where Quantum Matters</h2>
            <p className="body-large text-gray-300 max-w-4xl mx-auto">
              We collaborate under NDA with enterprise, academia, and government partners across high‑impact verticals 
              where quantum computing delivers measurable advantage.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {mockData.applicationDomains.map((domain, index) => (
              <motion.div
                key={domain}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-center text-sm hover:border-emerald-500/30 transition-colors"
              >
                {domain}
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <p className="body-medium text-gray-400 mb-6">
              Ready to explore quantum advantage in your domain?
            </p>
            <Button 
              className="btn-primary dark-button-animate"
              onClick={() => document.getElementById('waitlist').scrollIntoView({ behavior: 'smooth' })}
            >
              Schedule Technical Discussion
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Partners & Collaborations */}
      <section id="partners" className="py-24 border-t border-white/5 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="display-medium">Partners & Collaborations</h2>
              <p className="body-large text-gray-300 mt-4">Building the quantum ecosystem together</p>
            </motion.div>
            {/* Removed NDA placeholder text */}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6 mb-12">
            {mockData.partners.map((partner, index) => (
              <motion.div
                key={partner.id}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="aspect-[3/2] rounded-2xl border border-white/10 bg-gradient-to-br from-white/5 to-white/0 flex items-center justify-center text-xs text-gray-400 hover:border-emerald-500/30 transition-colors"
              >
                {partner.logo}
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <p className="body-medium text-gray-400 mb-6">
              Interested in strategic partnership or collaboration?
            </p>
            <Button 
              className="btn-secondary"
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            >
              Get in Touch
              <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Waitlist Section */}
      <section id="waitlist" className="py-24">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="display-medium mb-6">Join the Design‑Partner Waitlist</h2>
            <p className="body-large text-gray-300 mb-12">
              For investors, researchers, and enterprise teams exploring quantum pilots in 2025–2026. 
              We'll reach out under NDA with exclusive access and collaboration opportunities.
            </p>

            {!submitted ? (
              <motion.form
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                onSubmit={handleWaitlistSubmit}
                className="space-y-6"
              >
                <div className="grid sm:grid-cols-[1fr_auto] gap-4">
                  <input
                    type="email"
                    required
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-6 py-4 bg-black/50 border border-white/20 rounded-2xl text-white placeholder-gray-400 focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-400/20 transition-colors"
                  />
                  <Button 
                    type="submit"
                    className="btn-primary px-8 py-4 whitespace-nowrap"
                  >
                    Request Access
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
                <p className="text-xs text-gray-500">
                  By submitting, you agree to our Privacy Notice and NDA terms for design partners.
                </p>
              </motion.form>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="bg-emerald-500/10 border border-emerald-400/30 rounded-2xl p-8 text-emerald-300"
              >
                <div className="text-2xl font-semibold mb-2">Thank you!</div>
                <p>We'll be in touch soon with next steps and NDA details.</p>
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 border-t border-white/5 bg-white/[0.02]">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="display-medium mb-6">Frequently Asked Questions</h2>
            <p className="body-large text-gray-300">
              Everything you need to know about Godquantum Systems and our Quantum OS
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <Accordion type="single" collapsible className="space-y-4">
              {mockData.faq.map((item, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-black/40 border border-white/10 rounded-2xl px-6 py-2 hover:border-white/20 transition-colors"
                >
                  <AccordionTrigger className="text-left heading-3 text-white hover:no-underline">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="body-medium text-gray-300 pt-2 pb-4">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <p className="body-medium text-gray-400 mb-6">
              Have more questions? We'd love to hear from you.
            </p>
            <Button 
              className="btn-secondary"
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            >
              Contact Us
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="display-medium mb-6">Contact</h2>
            <p className="body-large text-gray-300">
              Ready to build the future of quantum computing together?
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* General Contact */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <Card className="p-8 bg-white/[0.02] border-white/10 dark-hover dark-transition h-full">
                <div className="text-emerald-400 mb-4">
                  <Building className="w-8 h-8" />
                </div>
                <h3 className="heading-2 mb-4 text-white">General</h3>
                <div className="space-y-3 text-gray-300">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-emerald-400" />
                    <span className="body-medium">{mockData.contact.general.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-emerald-400" />
                    <span className="body-medium">{mockData.contact.general.location}</span>
                  </div>
                </div>
              </Card>
            </motion.div>

            {/* Investors */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <Card className="p-8 bg-white/[0.02] border-white/10 dark-hover dark-transition h-full">
                <div className="text-emerald-400 mb-4">
                  <Briefcase className="w-8 h-8" />
                </div>
                <h3 className="heading-2 mb-4 text-white">Investors</h3>
                <p className="body-medium text-gray-300 mb-6">
                  {mockData.contact.investors.description}
                </p>
                <Button 
                  className="btn-secondary w-full"
                  onClick={() => document.getElementById('waitlist').scrollIntoView({ behavior: 'smooth' })}
                >
                  {mockData.contact.investors.action}
                </Button>
              </Card>
            </motion.div>

            {/* Careers */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <Card className="p-8 bg-white/[0.02] border-white/10 dark-hover dark-transition h-full">
                <div className="text-emerald-400 mb-4">
                  <Users className="w-8 h-8" />
                </div>
                <h3 className="heading-2 mb-4 text-white">Careers</h3>
                <p className="body-medium text-gray-300 mb-6">
                  {mockData.contact.careers.description}
                </p>
                <Button 
                  className="btn-secondary w-full"
                  onClick={() => window.open('https://forms.gle/85WmvhX9xBAtUiJH7', '_blank')}
                >
                  {mockData.contact.careers.action}
                </Button>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="flex items-center gap-6"
            >
              <div className="flex-shrink-0">
                <div className="logo-container">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_a1f0c07e-de70-48cd-86ab-b2b31bd62f8b/artifacts/w8bj5ad5_godquantum_logo_typograph.png"
                    alt="Godquantum Systems - Adding Qubits to Life"
                    className="logo-magnifier h-12 w-auto object-contain filter brightness-110"
                    title="Hover to read tagline: Adding Qubits to Life"
                  />
                </div>
              </div>
              <div className="text-sm text-gray-400">
                <div className="font-medium text-gray-300">© {new Date().getFullYear()} Godquantum Systems</div>
                <div className="text-gray-500 text-xs mt-1">All rights reserved</div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="flex flex-wrap gap-6 text-sm"
            >
              <a href="/privacy-policy" className="text-gray-400 hover:text-emerald-300 transition-colors">Privacy</a>
              <a href="/terms-of-service" className="text-gray-400 hover:text-emerald-300 transition-colors">Terms</a>
              <a href="/responsible-disclosure" className="text-gray-400 hover:text-emerald-300 transition-colors">Responsible Disclosure</a>
            </motion.div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default GodquantumLandingPart2;